import React, { useEffect, useMemo } from 'react'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { PlannedPowerMeterModel, PowerMeterForm, PowerMeterFormDeviceType } from 'src/Libs/installations/models'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { usePortalMutation } from 'src/Libs/api'
import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form'
import { getNextAvailableAddress } from 'src/Libs/installations/utils/create-powermeter'
import { Box, Stack, Typography, Button, useMediaQuery, useTheme } from '@mui/material'
import { SettingsBox } from 'src/Elements/Data'
import { Select, TabButtonGroup, TextField } from 'src/Libs/inputs/react-hook-form'
import { SaveConfigurationButton } from 'src/Libs/installations/components/new-installation/configuration/general'
import { createPlannedPowerMeter, updatePlannedPowerMeter } from 'src/Libs/installations/api'
import { usePowermeterFormInitializer } from 'src/Libs/installations'
import { usePowerMeterLastScanIpAddress } from 'src/Libs/installations/hooks/installation-configuration/power-meters/use-power-meter-last-scan-ip-address'
import { RadarOutline, J2Delete } from '@e3dc-react/icons'
import useModals from 'src/Hooks/useModals'
import { usePowerMeterSerialNumberReservation } from 'src/Libs/installations/hooks/installation-configuration/power-meters/use-power-meter-serial-number-reservation'
import {
  CURRENT_TRANSFORMER_RATIO_OPTIONS,
  hasExistingInternalOrExternalRootPowerMeter,
  METER_TYPE_INTERNAL_ROOT_POWER_METER,
} from 'src/Libs/installations/utils/power-meter-type'

interface PowerMeterFormComponentProps {
  /** Stable identifier of this form instance (e.g. the power meter id, or 'new'), used to share serial number selections across open, unsaved forms. */
  tileKey: string
  installationId?: number
  emcDeviceLinkId: string | null
  emcSerialnumber: string | null
  allPowermeters: Nullable<PlannedPowerMeterModel>[]
  onSuccess: () => void
  powermeter?: PlannedPowerMeterModel
}

interface CreatePowerMeterVariables {
  installationId: number
  emcDeviceLinkId: string
  payload: PowerMeterForm
}

interface SavePowerMeterVariables extends CreatePowerMeterVariables {
  powermeter: PlannedPowerMeterModel
}

const PowerMeterFormCompoent: React.FC<PowerMeterFormComponentProps> = ({
  tileKey,
  installationId,
  emcSerialnumber,
  emcDeviceLinkId,
  allPowermeters,
  onSuccess,
  powermeter,
}) => {
  const { t } = useCustomTranslation()
  const { openPowerMeterScanDialog } = useModals()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const authFetch = useAuthFetchWithResponse()
  const { validationSchema, initialValues } = usePowermeterFormInitializer(powermeter, allPowermeters)
  const methods = useForm<PowerMeterForm>({
    defaultValues: initialValues,
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
  })
  const { reservedSerialNumbersByOtherTiles, reserveSerialNumber } = usePowerMeterSerialNumberReservation(tileKey)
  const watchedSerialNumber = useWatch({ control: methods.control, name: 'serialNumber' })
  const watchedName = useWatch({ control: methods.control, name: 'name' })

  /** Device names of all existing power meters, keyed by their assigned serial number, so the scan dialog can flag already assigned devices. */
  const linkedPowerMetersBySerialNumber = useMemo(() => {
    const map: Record<string, string> = {}

    for (const meter of allPowermeters) {
      if (!meter) continue

      const serialNumber = meter.mediaParameters?.serialNumber?.trim()
      if (serialNumber && meter.deviceName) {
        map[serialNumber] = meter.deviceName
      }
    }
    return map
  }, [allPowermeters])

  /** Combines serial numbers already saved on other power meters with those currently (but not yet) selected in other open forms. */
  const unavailableSerialNumbersBySerialNumber = useMemo(
    () => ({ ...linkedPowerMetersBySerialNumber, ...reservedSerialNumbersByOtherTiles }),
    [linkedPowerMetersBySerialNumber, reservedSerialNumbersByOtherTiles]
  )

  /** Keeps the shared reservation in sync with this form's current, possibly unsaved, serial number selection. */
  useEffect(() => {
    reserveSerialNumber(watchedSerialNumber || null, watchedName || t('installation-configuration.power-meters.new-powermeter'))
  }, [watchedSerialNumber, watchedName, reserveSerialNumber, t])

  /** Releases the reservation once this form is unmounted (e.g. the new power meter panel is closed without saving). */
  useEffect(() => {
    return () => reserveSerialNumber(null, '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleScanClick = (): void => {
    if (!emcSerialnumber || !installationId || !emcDeviceLinkId) return
    openPowerMeterScanDialog({
      emcSerialnumber,
      onSelect: (serialNumber, ipAddress, index) => {
        methods.setValue('index', index, { shouldDirty: true, shouldValidate: true })
        methods.setValue('serialNumber', serialNumber, { shouldDirty: true, shouldValidate: true })
        methods.setValue('powerMeterIpAddress', ipAddress ?? '', { shouldDirty: true, shouldValidate: true })
      },
      linkedPowerMetersBySerialNumber: unavailableSerialNumbersBySerialNumber,
    })
  }

  usePowerMeterLastScanIpAddress(emcSerialnumber, watchedSerialNumber, methods.setValue, methods.getFieldState)

  const createPowerMeterMutation = usePortalMutation<string | null, unknown, CreatePowerMeterVariables>({
    mutationFn: ({ installationId, emcDeviceLinkId, payload }) => createPlannedPowerMeter(installationId, emcDeviceLinkId, payload, authFetch),
    onSuccess: () => {
      enqueueSuccessStack(t('installation-configuration.power-meters.save.success'))
      onSuccess()
    },
    onError: error => {
      enqueueErrorStack(t('installation-configuration.general-settings.save.error'))
      console.error('error creating power meter', error)
    },
  })

  const savePowerMeterMutation = usePortalMutation<null, unknown, SavePowerMeterVariables>({
    mutationFn: ({ installationId, emcDeviceLinkId, payload, powermeter }) =>
      updatePlannedPowerMeter(installationId, emcDeviceLinkId, powermeter, payload, authFetch),
    onSuccess: () => {
      enqueueSuccessStack(t('installation-configuration.power-meters.save.success'))
      onSuccess()
    },
    onError: error => {
      enqueueErrorStack(t('installation-configuration.general-settings.save.error'))
      console.error('error saving power meter', error)
    },
  })

  const onSubmit = (data: PowerMeterForm): void => {
    if (!installationId || !emcDeviceLinkId) {
      enqueueErrorStack(t('installation-configuration.general-settings.save.error'))
      return
    }

    if (powermeter) {
      savePowerMeterMutation.mutate({
        installationId,
        emcDeviceLinkId,
        payload: data,
        powermeter,
      })
      return
    }

    const nextAvailableAddress = getNextAvailableAddress(allPowermeters)
    if (nextAvailableAddress === null) {
      enqueueErrorStack(t('installation-configuration.general-settings.save.error'))
      return
    }

    createPowerMeterMutation.mutate({
      installationId,
      emcDeviceLinkId,
      payload: {
        ...data,
      },
    })
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <Stack gap={2}>
          <SettingsBox
            title={'common.name'}
            gridSizes={{ smLeft: 6, smRight: 6 }}
            suffix={<TextField name="name" label={t('common.name')} fullWidth />}
          />
        </Stack>
        <PowerMeterScan
          onScanClick={handleScanClick}
          emcSerialnumber={emcSerialnumber}
          installationId={installationId}
          emcDeviceLinkId={emcDeviceLinkId}
        />
        <PowerMeterIpAddress />
        <PowerMeterType powermeter={powermeter} allPowermeters={allPowermeters} />
        {/* CT controls and their EMC config save stay disabled until configuration via RSCP is supported. */}
        {/* <ConnectionDirection />
        <CurrentTransformer /> */}
        <SaveConfigurationButton isLoading={createPowerMeterMutation.isPending || savePowerMeterMutation.isPending} />
      </form>
    </FormProvider>
  )
}

export default PowerMeterFormCompoent

//#region Power Meter Type

interface PowerMeterTypeProps {
  powermeter?: PlannedPowerMeterModel
  allPowermeters: Nullable<PlannedPowerMeterModel>[]
}

const PowerMeterType: React.FC<PowerMeterTypeProps> = ({ powermeter, allPowermeters }) => {
  const { t } = useCustomTranslation()
  const rootPowerMeterAlreadyExists = hasExistingInternalOrExternalRootPowerMeter(allPowermeters, powermeter)

  return (
    <Stack gap={2}>
      <SettingsBox
        title={'installation-configuration.powermeter.meter-type.title'}
        gridSizes={{ smLeft: 6, smRight: 6 }}
        suffix={
          <Select
            name={'deviceType'}
            label={t('installation-configuration.powermeter.meter-type.title')}
            options={[
              {
                value: METER_TYPE_INTERNAL_ROOT_POWER_METER,
                label: t('installation-configuration.powermeter.meter-type.internal-root-power-meter'),
                disabled: rootPowerMeterAlreadyExists,
              },
              {
                value: PowerMeterFormDeviceType.ExternalRootPowerMeter,
                label: t('installation-configuration.powermeter.meter-type.external-root-power-meter'),
                disabled: rootPowerMeterAlreadyExists,
              },
              { value: PowerMeterFormDeviceType.ProductionMeter, label: t('installation-configuration.powermeter.meter-type.production-meter') },
              { value: PowerMeterFormDeviceType.ConsumptionMeter, label: t('installation-configuration.powermeter.meter-type.consumption-meter') },
              {
                value: PowerMeterFormDeviceType.BidirectionalMeter,
                label: t('installation-configuration.powermeter.meter-type.bidirectional-meter'),
              },
              { value: PowerMeterFormDeviceType.ThirdPartyMeter, label: t('installation-configuration.powermeter.meter-type.third-party-meter') },
              { value: PowerMeterFormDeviceType.ControllerBypass, label: t('installation-configuration.powermeter.meter-type.controller-bypass') },
              {
                value: PowerMeterFormDeviceType.ProductionMeterWithoutStorage,
                label: t('installation-configuration.powermeter.meter-type.production-meter-without-storage'),
              },
            ]}
            fullWidth
          />
        }
      />
    </Stack>
  )
}
//#endregion

//#region Power meter scan
interface PowerMeterScanProps {
  onScanClick: () => void
  emcSerialnumber: string | null
  installationId?: number
  emcDeviceLinkId: string | null
}

const PowerMeterScan: React.FC<PowerMeterScanProps> = ({ onScanClick, emcSerialnumber, installationId, emcDeviceLinkId }) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'))
  const { control, setValue } = useFormContext<PowerMeterForm>()
  const serialNumber = useWatch({ control, name: 'serialNumber' })

  const handleClearSerialNumber = (): void => {
    setValue('index', undefined, { shouldDirty: true, shouldValidate: true })
    setValue('serialNumber', '', { shouldDirty: true, shouldValidate: true })
    setValue('powerMeterIpAddress', '', { shouldDirty: true, shouldValidate: true })
  }

  return (
    <SettingsBox
      title={t('installation-configuration.powermeter.scan.title')}
      gridSizes={{ smLeft: 6, smRight: 6 }}
      suffix={
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          gap={2}
          alignItems={{ xs: 'stretch', lg: 'center' }}
          justifyContent="flex-end"
          sx={{ width: '100%' }}
        >
          <TextField name="serialNumber" label={t('installation-configuration.powermeter.scan.serial-number')} disabled fullWidth />
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} sx={{ width: { xs: '100%', lg: 'auto' } }}>
            <Button
              variant="outlined"
              onClick={handleClearSerialNumber}
              disabled={!serialNumber}
              startIcon={<J2Delete fontSize="small" />}
              fullWidth={isDownMd}
              sx={{ px: 3, minWidth: { xs: '174px', sm: 0, md: '174px' }, flex: { sm: '1 1 0', lg: '0 0 auto' } }}
            >
              {t('installation-configuration.powermeter.delete-button')}
            </Button>
            <Button
              variant="outlined"
              onClick={onScanClick}
              disabled={!emcSerialnumber || !installationId || !emcDeviceLinkId}
              startIcon={<RadarOutline fontSize="small" />}
              fullWidth={isDownMd}
              sx={{ minWidth: { xs: '174px', sm: 0, md: '174px' }, flex: { sm: '1 1 0', lg: '0 0 auto' } }}
            >
              {t('installation-configuration.powermeter.scan-button')}
            </Button>
          </Stack>
        </Stack>
      }
    />
  )
}
// #endregion

//#region Power meter ip adress
const PowerMeterIpAddress: React.FC = () => {
  const { t } = useCustomTranslation()

  return (
    <SettingsBox
      title={t('installation-configuration.powermeter.ipAddress.title')}
      gridSizes={{ smLeft: 6, smRight: 6 }}
      suffix={
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          gap={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="flex-end"
          sx={{ width: '100%' }}
        >
          <TextField name="powerMeterIpAddress" label={t('installation-configuration.powermeter.ipAddress')} disabled fullWidth />
        </Stack>
      }
    />
  )
}
// #endregion

//#region Connection direction

// eslint-disable-next-line
const ConnectionDirection: React.FC = () => {
  const { t } = useCustomTranslation()

  return (
    <Stack gap={2}>
      <SettingsBox
        title={t('installation-configuration.powermeter.connection-direction-title')}
        gridSizes={{ smLeft: 6, smRight: 6 }}
        suffix={
          <Box sx={{ width: { xs: '100%', md: 280 } }}>
            <TabButtonGroup
              name={'wiringMode'}
              options={[
                { value: 'Normal', label: t('installation-configuration.powermeter.connection-direction.normal') },
                { value: 'Inverted', label: t('installation-configuration.powermeter.connection-direction.inverted') },
              ]}
            />
          </Box>
        }
      />
    </Stack>
  )
}
//#endregion

//#region Current Transformer

// eslint-disable-next-line
const CurrentTransformer: React.FC = () => {
  const { t } = useCustomTranslation()
  const deviceType = useWatch({ name: 'deviceType' })
  const isDisabled = deviceType !== METER_TYPE_INTERNAL_ROOT_POWER_METER
  const useCT = useWatch({ name: 'useCT' })

  return (
    <Stack gap={2}>
      <SettingsBox
        title={'installation-configuration.powermeter.current-transformer.title'}
        gridSizes={{ smLeft: 6, smRight: 6 }}
        suffix={
          <Box sx={{ width: { xs: '100%', md: 280 } }}>
            <TabButtonGroup
              name={'useCT'}
              disabled={isDisabled}
              options={[
                { value: true, label: t('installation-configuration.powermeter.current-transformer.on') },
                { value: false, label: t('installation-configuration.powermeter.current-transformer.off') },
              ]}
            />
          </Box>
        }
      >
        {!isDisabled && useCT && (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'shades.shade03',
              backgroundColor: 'white',
              borderRadius: 2,
              p: { xs: 2, md: 3 },
            }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent={'space-between'} alignItems={{ xs: 'stretch', md: 'center' }} gap={2}>
              <Typography variant={'body2'}>{t('installation-configuration.powermeter.current-transformer.ratio')}</Typography>
              <Box sx={{ width: { xs: '100%', md: 240 } }}>
                <Select
                  name={'ratioCT'}
                  label={t('installation-configuration.powermeter.current-transformer.ratio')}
                  options={CURRENT_TRANSFORMER_RATIO_OPTIONS}
                  disabled={isDisabled}
                  fullWidth
                />
              </Box>
            </Stack>
          </Box>
        )}
      </SettingsBox>
    </Stack>
  )
}
//#endregion
